// AI Model management — backed by /admin/ai/models API.
import { useEffect, useState } from "react";
import { api } from "../../../shared/api/client";
import { Empty, Loading } from "../../../shared/components/Empty";
import { Pill } from "../../../shared/components/Pill";
import { toast } from "../../../shared/stores/toasts";

type Model = {
  id: number; name: string; display_name?: string | null; provider: string;
  status: "ACTIVE" | "INACTIVE"; endpoint_url?: string | null;
  is_default: boolean; has_api_key: boolean;
  context_tokens: number; input_cost_per_1m: number; output_cost_per_1m: number;
  temperature: number; max_tokens: number; monthly_budget: number | null; rate_limit_rpm: number;
};

const EMPTY_FORM = {
  provider: "", name: "", display_name: "", api_key: "", endpoint_url: "",
  context_tokens: 4096, input_cost_per_1m: 0, output_cost_per_1m: 0,
  temperature: 0.7, max_tokens: 4096, rate_limit_rpm: 60, monthly_budget: "",
};

// 텍스트 입력 필드 정의 (표기명 추가)
const TEXT_FIELDS: { k: keyof typeof EMPTY_FORM; label: string; ph?: string }[] = [
  { k: "provider", label: "공급자", ph: "gemini / google / deepl / claude-cli / ollama …" },
  { k: "name", label: "모델명 (API 호출용)", ph: "예: gemini-3.5-flash" },
  { k: "display_name", label: "표기명 (화면 표시용)", ph: "예: Gemini 기본키" },
  { k: "api_key", label: "API 키" },
  { k: "endpoint_url", label: "엔드포인트 URL" },
];

type OllamaModel = { name: string; size: number };

// 다른 프로그램(커스텀 앱)에서 AI/번역을 호출하는 표준 패턴 — 키는 서버에만 둠.
const BRIDGE_SNIPPET = `// 커스텀 앱 frontend.html 에서 — 샌드박스 iframe, API 키 없이 브리지로 호출
// 1) 사용 가능한 모델 목록 (표기명 포함)
TimBox.aiModels(models => {
  // models: [{ id, name, display_name, is_default, supports_image }]
});

// 2) 텍스트 번역 (model_id 생략 시 기본 모델 + 자동 키 페일오버)
TimBox.translate({ text: "안녕하세요", source: "auto", target: "en", model_id }, res => {
  if (res.error) showError(res.error);
  else use(res.result);   // { result, model_name }
});

// 3) 이미지 OCR 번역 (Gemini 모델만 supports_image)
TimBox.translate({ image_base64: b64, mime: "image/png", target: "ko" }, cb);

// 4) 사용자 정보 / 프로그램 데이터 저장
TimBox.me(u => { /* { id, username, name, roles, admin } */ });
TimBox.load(d => {/* 불러오기 */});  TimBox.save({ any: "json" }, cb);`;

export function AdminAi() {
  const [models, setModels] = useState<Model[] | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [ollama, setOllama] = useState<OllamaModel[] | null>(null);
  const [ollamaLoading, setOllamaLoading] = useState(false);

  const reload = () =>
    api.get<Model[]>("/admin/ai/models")
      .then((r) => setModels(r.data))
      .catch(() => setModels([]));

  useEffect(() => { reload(); }, []);

  const toggle = async (id: number, current: string) => {
    try {
      await api.patch(`/admin/ai/models/${id}`, { status: current === "ACTIVE" ? "INACTIVE" : "ACTIVE" });
      reload();
    } catch (e: any) { toast.error("실패", e?.response?.data?.error?.message); }
  };

  const setDefault = async (id: number) => {
    try { await api.patch(`/admin/ai/models/${id}`, { is_default: true }); toast.success("기본 모델 변경"); reload(); }
    catch (e: any) { toast.error("실패", e?.response?.data?.error?.message); }
  };

  const remove = async (id: number) => {
    if (!confirm("이 모델을 삭제하시겠습니까?")) return;
    try { await api.delete(`/admin/ai/models/${id}`); toast.success("삭제됨"); reload(); }
    catch (e: any) { toast.error("삭제 실패", e?.response?.data?.error?.message); }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (m: Model) => {
    setForm({
      provider: m.provider, name: m.name, display_name: m.display_name ?? "",
      api_key: "", endpoint_url: m.endpoint_url ?? "",
      context_tokens: m.context_tokens, input_cost_per_1m: m.input_cost_per_1m,
      output_cost_per_1m: m.output_cost_per_1m, temperature: m.temperature,
      max_tokens: m.max_tokens, rate_limit_rpm: m.rate_limit_rpm,
      monthly_budget: m.monthly_budget != null ? String(m.monthly_budget) : "",
    });
    setEditingId(m.id);
    setShowForm(true);
  };

  const cancelForm = () => { setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); };

  const seedDefaults = async () => {
    setSeeding(true);
    try {
      const r = await api.post<{ created: number; skipped: number }>("/admin/ai/models/seed-defaults");
      toast.success(`기본 모델 등록 완료`, `추가: ${r.data.created}, 이미 있음: ${r.data.skipped}`);
      reload();
    } catch (e: any) {
      toast.error("시드 실패", e?.response?.data?.detail ?? e?.response?.data?.error?.message);
    } finally {
      setSeeding(false);
    }
  };

  const discoverOllama = async () => {
    setOllamaLoading(true);
    try {
      const r = await api.get<OllamaModel[]>("/admin/ai/ollama/models");
      setOllama(r.data);
      if (r.data.length === 0) toast.info("Ollama 모델 없음", "http://localhost:11434 에서 모델을 찾을 수 없습니다");
    } catch (e: any) {
      toast.error("Ollama 검색 실패", e?.response?.data?.error?.message);
    } finally {
      setOllamaLoading(false);
    }
  };

  const addOllamaModel = async (name: string) => {
    try {
      await api.post("/admin/ai/models", {
        provider: "ollama",
        name,
        endpoint_url: "http://localhost:11434",
        context_tokens: 8192,
        temperature: 0.7,
        max_tokens: 4096,
        rate_limit_rpm: 60,
      });
      toast.success(`Ollama 모델 추가됨: ${name}`);
      reload();
    } catch (e: any) {
      toast.error("추가 실패", e?.response?.data?.detail ?? e?.response?.data?.error?.message);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const body: any = {
        ...form,
        endpoint_url: form.endpoint_url || null,
        monthly_budget: form.monthly_budget ? Number(form.monthly_budget) : null,
      };
      if (editingId !== null) {
        // 수정: API 키는 입력했을 때만 변경(비우면 기존 키 유지)
        if (!form.api_key) delete body.api_key;
        await api.patch(`/admin/ai/models/${editingId}`, body);
        toast.success("모델 수정됨");
      } else {
        body.api_key = form.api_key || null;
        await api.post("/admin/ai/models", body);
        toast.success("모델 추가됨");
      }
      cancelForm();
      reload();
    } catch (e: any) {
      toast.error(editingId !== null ? "수정 실패" : "추가 실패", e?.response?.data?.detail ?? e?.response?.data?.error?.message);
    } finally {
      setSaving(false);
    }
  };

  if (models === null) return <Loading/>;

  return (
    <div className="t-grid cols-1">
      <div className="t-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <span style={{ fontWeight: 600 }}>AI 모델 ({models.length})</span>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="t-btn" onClick={() => setShowGuide((v) => !v)} title="페일오버 동작 + 다른 프로그램에서 AI 연동하는 방법">
            📖 개발 참조
          </button>
          <button className="t-btn" onClick={seedDefaults} disabled={seeding} title="Gemini · Claude · Google 번역 · DeepL 를 env 설정에서 자동 등록">
            {seeding ? "등록 중…" : "✨ 기본 모델 등록"}
          </button>
          <button className="t-btn" onClick={discoverOllama} disabled={ollamaLoading} title="localhost:11434 에서 Ollama 모델 자동 검색">
            {ollamaLoading ? "검색 중…" : "🦙 Ollama 검색"}
          </button>
          <button className="t-btn solid" onClick={() => { if (showForm && editingId === null) cancelForm(); else openCreate(); }}>
            {showForm && editingId === null ? "취소" : "+ 직접 추가"}
          </button>
        </div>
      </div>

      {/* 개발 참조 패널 — 페일오버 동작 + 다른 프로그램에서 AI 연동하는 법 */}
      {showGuide && (
        <div className="t-card" style={{ fontSize: 13, lineHeight: 1.7 }}>
          <h4 style={{ margin: "0 0 8px" }}>📖 AI 연동 개발 참조</h4>

          <p style={{ margin: "10px 0 4px", fontWeight: 600 }}>① API 키 자동 페일오버</p>
          <ul style={{ margin: "0 0 8px", paddingLeft: 18, color: "var(--text-2)" }}>
            <li>같은 공급자(예: gemini) 모델을 <b>여러 개</b> 등록하면, 한 키가 사용량 한도(429)에 걸릴 때 <b>다음 키로 자동 전환</b>됩니다.</li>
            <li>우선순위: <b>기본 모델 → 등록순</b>. 한도에 걸린 키는 그날(KST 자정)까지 건너뛰고, <b>자정 이후 기본 키부터 자동 재사용</b>합니다.</li>
            <li>채팅 어시스턴트(<code>/ai/chat</code>)와 번역기(<code>/ai/translate</code>) <b>모두</b> 동일하게 적용됩니다.</li>
            <li>예비 키 추가: <b>+ 직접 추가</b> → 공급자 <code>gemini</code>, 모델명 <code>gemini-3.5-flash</code>, API 키 입력.</li>
          </ul>

          <p style={{ margin: "10px 0 4px", fontWeight: 600 }}>② 표기명(display_name)</p>
          <ul style={{ margin: "0 0 8px", paddingLeft: 18, color: "var(--text-2)" }}>
            <li><b>모델명</b>은 API 호출 식별자(<code>gemini-3.5-flash</code>), <b>표기명</b>은 화면 표시용 별칭(<code>Gemini 예비키</code>)입니다.</li>
            <li>각 모델의 <b>✏️ 수정</b>에서 설정하면 목록·번역기 드롭다운에 표기명으로 보입니다(없으면 모델명).</li>
          </ul>

          <p style={{ margin: "10px 0 4px", fontWeight: 600 }}>③ 다른 프로그램(커스텀 앱)에서 AI 사용하기</p>
          <p style={{ margin: "0 0 6px", color: "var(--text-2)" }}>
            커스텀 앱은 샌드박스 iframe이라 키를 가질 수 없습니다. <code>TimBox</code> 브리지로 서버에 요청하면
            키 관리·페일오버가 자동 적용됩니다.
          </p>
          <pre style={{ background: "var(--bg-0)", border: "1px solid var(--border)", borderRadius: 8,
                        padding: 12, fontSize: 12, overflow: "auto", margin: 0,
                        fontFamily: "ui-monospace, monospace", whiteSpace: "pre" }}>{BRIDGE_SNIPPET}</pre>

          <p style={{ margin: "10px 0 4px", fontWeight: 600 }}>④ 백엔드 엔드포인트</p>
          <ul style={{ margin: 0, paddingLeft: 18, color: "var(--text-2)" }}>
            <li><code>POST /api/v1/ai/translate</code> — 텍스트/이미지 번역(모델 선택+페일오버)</li>
            <li><code>GET /api/v1/ai/translate/models</code> — 모델 목록(표기명 포함, 키 미노출)</li>
            <li><code>POST /api/v1/ai/chat</code> — 채팅(동일 페일오버 적용)</li>
            <li>코드: <code>backend/app/modules/ai/chat_router.py</code> (<code>_gemini_failover</code>), 브리지: <code>frontend/src/pages/extras/CustomAppView.tsx</code></li>
            <li>상세 문서: <code>docs/AI연동_가이드.md</code></li>
          </ul>
        </div>
      )}

      {/* Ollama discovered models */}
      {ollama !== null && ollama.length > 0 && (
        <div className="t-card">
          <h4 style={{ margin: "0 0 10px" }}>🦙 발견된 Ollama 모델</h4>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {ollama.map((m) => {
              const already = models.some((x) => x.provider === "ollama" && x.name === m.name);
              return (
                <div key={m.name} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ flex: 1, fontFamily: "monospace", fontSize: 13 }}>{m.name}</span>
                  <span style={{ fontSize: 11, color: "var(--text-3)" }}>
                    {m.size ? `${(m.size / 1e9).toFixed(1)} GB` : ""}
                  </span>
                  {already ? (
                    <span style={{ fontSize: 12, color: "var(--text-3)" }}>등록됨</span>
                  ) : (
                    <button className="t-btn solid" style={{ fontSize: 12 }} onClick={() => addOllamaModel(m.name)}>
                      추가
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {ollama !== null && ollama.length === 0 && (
        <div className="t-card" style={{ color: "var(--text-3)", fontSize: 13 }}>
          ℹ️ Ollama가 실행 중이지 않거나 설치된 모델이 없습니다. <code>ollama pull llama3</code> 등으로 모델을 설치하세요.
        </div>
      )}

      {showForm && (
        <div className="t-card" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <h4 style={{ margin: 0, gridColumn: "1/-1" }}>{editingId !== null ? "모델 수정" : "새 모델 추가"}</h4>
          {(form.provider === "google" || form.provider === "deepl") && (
            <div style={{ gridColumn: "1/-1", fontSize: 12, color: "var(--text-2)", background: "var(--bg-0)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 12px", lineHeight: 1.7 }}>
              {form.provider === "google"
                ? <>🌐 <b>Google 번역</b>: 모델명은 자유롭게(예: <code>Google 번역</code>). <b>API 키</b>에 Google Cloud Translation API 키를 입력하세요(Cloud Translation API 활성화 필요). 글자 단위 과금, 이미지 OCR은 미지원.</>
                : <>🟦 <b>DeepL</b>: 모델명은 자유롭게(예: <code>DeepL</code>). <b>API 키</b>에 DeepL 키 입력(무료 키는 <code>:fx</code>로 끝남 → 자동으로 free 엔드포인트 사용). 한국어·영어·일본어·중국어·유럽어 등 고품질, 일부 언어(베트남어·태국어·아랍어·힌디어)는 미지원.</>}
            </div>
          )}
          {TEXT_FIELDS.map(({ k, label, ph }) => (
            <div key={k}>
              <label className="t-label">{label}</label>
              <input
                className="t-input"
                type={k === "api_key" ? "password" : "text"}
                placeholder={k === "api_key" && editingId !== null ? "변경 시에만 입력 (비우면 기존 키 유지)" : (ph ?? "")}
                value={(form as any)[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                required={k === "provider" || k === "name"}
              />
            </div>
          ))}
          {(["context_tokens","input_cost_per_1m","output_cost_per_1m","temperature","max_tokens","rate_limit_rpm","monthly_budget"] as const).map((k) => (
            <div key={k}>
              <label className="t-label">{k}</label>
              <input className="t-input" type="number" step="any" value={(form as any)[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value as any })}/>
            </div>
          ))}
          <div style={{ gridColumn: "1/-1", display: "flex", gap: 8 }}>
            <button className="t-btn solid" onClick={save} disabled={saving || !form.provider || !form.name}>
              {saving ? "저장 중…" : (editingId !== null ? "저장" : "추가")}
            </button>
            <button className="t-btn" onClick={cancelForm} disabled={saving}>취소</button>
          </div>
        </div>
      )}

      {models.length === 0 && <Empty msg="등록된 AI 모델이 없습니다"/>}

      <div className="t-grid cols-2">
        {models.map((m) => (
          <div key={m.id} className="t-card">
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
              <h3 style={{ margin: 0 }}>{m.display_name || m.name}</h3>
              <Pill kind="info">{m.provider}</Pill>
              <Pill kind={m.status === "ACTIVE" ? "ok" : "muted"}>{m.status}</Pill>
              {m.is_default && <Pill kind="violet">기본</Pill>}
            </div>
            {m.display_name && (
              <div style={{ fontSize: 11, color: "var(--text-3)", fontFamily: "monospace", marginBottom: 8 }}>{m.name}</div>
            )}
            <div style={{ fontSize: 12, color: "var(--text-3)", lineHeight: 1.8, marginTop: 8 }}>
              <div>컨텍스트: <span className="num">{m.context_tokens.toLocaleString()}</span> tokens</div>
              <div>입력 비용: <span className="num">${m.input_cost_per_1m.toFixed(3)}</span> / 1M</div>
              <div>출력 비용: <span className="num">${m.output_cost_per_1m.toFixed(3)}</span> / 1M</div>
              <div>Temperature: <span className="num">{m.temperature}</span> · Max tokens: <span className="num">{m.max_tokens.toLocaleString()}</span></div>
              {m.monthly_budget && <div>월 예산: <span className="num">${m.monthly_budget.toFixed(2)}</span></div>}
              <div>API 키: {m.has_api_key ? "등록됨" : <span style={{ color: "var(--warn)" }}>미등록</span>}</div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button className="t-btn solid" style={{ fontSize: 12 }} onClick={() => openEdit(m)}>✏️ 수정</button>
              <button className="t-btn" style={{ fontSize: 12 }} onClick={() => toggle(m.id, m.status)}>
                {m.status === "ACTIVE" ? "비활성" : "활성"}
              </button>
              {!m.is_default && (
                <button className="t-btn" style={{ fontSize: 12 }} onClick={() => setDefault(m.id)}>기본으로</button>
              )}
              <button className="t-btn" style={{ fontSize: 12, marginLeft: "auto", color: "var(--danger, #ef4444)" }} onClick={() => remove(m.id)}>
                삭제
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
